import React, { useCallback, useEffect, useState } from 'react';
import { Tabs, Table, Tag, Select, Input, Space, Typography, Button, Switch, Popconfirm, message, Modal } from 'antd';
import { LinkOutlined, ReloadOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { importAliasAPI, supplierAPI, customerAPI, productAPI, customerOrderAPI, shippingDestinationAPI } from '../services/api.ts';
import { generateProductDisplayName } from '../utils/productUtils.ts';

const { Title, Text } = Typography;

/**
 * Impostazioni → Corrispondenze (solo admin): gli alias imparati dalle conferme nei
 * flussi di importazione da documento. Tre schede: prodotti del fornitore, clienti /
 * fornitori, sigle → destinazione. Qui si consultano, correggono, disattivano o
 * eliminano; non si creano dal nulla (nascono solo dalle conferme).
 */
const ImportAliases: React.FC = () => {
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [openOrders, setOpenOrders] = useState<any[]>([]);

  useEffect(() => {
    supplierAPI.getSuppliers().then(setSuppliers).catch(() => setSuppliers([]));
    customerAPI.getCustomers().then((d: any) => setCustomers(Array.isArray(d) ? d : d?.items || [])).catch(() => setCustomers([]));
    productAPI.getProducts().then((d: any) => setProducts(Array.isArray(d) ? d : d?.items || [])).catch(() => setProducts([]));
    customerOrderAPI.getCustomerOrders({ exclude_completed: true, limit: 1000 } as any)
      .then((d: any) => setOpenOrders(Array.isArray(d) ? d : d?.items || d?.orders || []))
      .catch(() => setOpenOrders([]));
  }, []);

  const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleString('it-IT') : '-');

  // ------------------------------------------------------------ prodotti
  const ProductsTab: React.FC = () => {
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [supplierId, setSupplierId] = useState<number | undefined>();
    const [search, setSearch] = useState('');
    const [inactive, setInactive] = useState(false);
    const [page, setPage] = useState(1);
    const [editing, setEditing] = useState<any | null>(null);
    const [newProduct, setNewProduct] = useState<number | undefined>();

    const load = useCallback(async () => {
      setLoading(true);
      try {
        const d = await importAliasAPI.listProducts({ supplier_id: supplierId, search: search || undefined, include_inactive: inactive, skip: (page - 1) * 20, limit: 20 });
        setRows(d.items || []); setTotal(d.total || 0);
      } catch (e: any) { message.error(e?.response?.data?.detail || 'Errore nel caricamento'); }
      finally { setLoading(false); }
    }, [supplierId, search, inactive, page]);
    useEffect(() => { load(); }, [load]);

    const toggle = async (r: any, active: boolean) => {
      try { await importAliasAPI.updateProduct(r.id, { is_active: active }); load(); }
      catch (e: any) { message.error(e?.response?.data?.detail || 'Errore'); }
    };
    const remove = async (r: any) => {
      try { await importAliasAPI.deleteProduct(r.id); message.success('Corrispondenza eliminata'); load(); }
      catch (e: any) { message.error(e?.response?.data?.detail || 'Errore'); }
    };
    const saveEdit = async () => {
      if (!editing || !newProduct) return;
      try { await importAliasAPI.updateProduct(editing.id, { product_id: newProduct }); message.success('Corrispondenza aggiornata'); setEditing(null); load(); }
      catch (e: any) { message.error(e?.response?.data?.detail || 'Errore'); }
    };

    return (
      <div>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select allowClear placeholder="Fornitore" style={{ width: 280 }} showSearch optionFilterProp="label"
            value={supplierId} onChange={(v) => { setSupplierId(v); setPage(1); }}
            options={suppliers.map((s: any) => ({ value: s.id, label: s.name }))} />
          <Input.Search placeholder="Testo o codice prodotto" allowClear style={{ width: 280 }} onSearch={(v) => { setSearch(v); setPage(1); }} />
          <span><Switch checked={inactive} onChange={setInactive} /> <Text type="secondary">mostra disattivate</Text></span>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Aggiorna</Button>
          <Text type="secondary">{total} corrispondenze</Text>
        </Space>
        <Table size="small" rowKey="id" loading={loading} dataSource={rows}
          pagination={{ current: page, pageSize: 20, total, onChange: setPage }}
          columns={[
            { title: 'Fornitore / Cliente', width: 200, ellipsis: true, render: (r: any) => r.supplier_name ? r.supplier_name : r.customer_name ? <span><Tag color="blue">cliente</Tag>{r.customer_name}</span> : '-' },
            { title: 'Testo nel documento', dataIndex: 'original_text', ellipsis: true,
              render: (v: string, r: any) => <span title={r.normalized_text}>{v || r.normalized_text}</span> },
            { title: 'Prodotto NAVI', width: 300, render: (r: any) => r.product ? (
              <span><b>{generateProductDisplayName(r.product.internal_product_code, r.product.size, r.product.color)}</b><br /><Text type="secondary" style={{ fontSize: 12 }}>{r.product.product_description}</Text></span>) : '-' },
            { title: 'Usi', dataIndex: 'use_count', width: 60, align: 'center' as const },
            { title: 'Ultimo uso', dataIndex: 'last_used_at', width: 140, render: fmtDate },
            { title: 'Confermata da', dataIndex: 'confirmed_by_username', width: 110, render: (v: string) => v || '-' },
            { title: 'Attiva', width: 70, render: (r: any) => <Switch size="small" checked={r.is_active} onChange={(v) => toggle(r, v)} /> },
            { title: '', width: 90, render: (r: any) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(r); setNewProduct(r.product_id); }} />
                <Popconfirm title="Eliminare la corrispondenza?" onConfirm={() => remove(r)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
              </Space>) },
          ]} />
        <Modal open={!!editing} title="Cambia prodotto della corrispondenza" onOk={saveEdit} onCancel={() => setEditing(null)} okText="Salva" cancelText="Annulla">
          {editing && (
            <div>
              <p><Text type="secondary">Testo:</Text> {editing.original_text || editing.normalized_text}</p>
              <Select style={{ width: '100%' }} showSearch optionFilterProp="label" value={newProduct} onChange={setNewProduct}
                options={products.map((p: any) => ({ value: p.id, label: `${generateProductDisplayName(p.internal_product_code, p.size, p.color)} - ${p.product_description || ''}` }))} />
            </div>
          )}
        </Modal>
      </div>
    );
  };

  // ------------------------------------------------------------ anagrafiche
  const PartiesTab: React.FC = () => {
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [kind, setKind] = useState<string | undefined>();
    const [search, setSearch] = useState('');
    const [inactive, setInactive] = useState(false);
    const [page, setPage] = useState(1);
    const [editing, setEditing] = useState<any | null>(null);
    const [newParty, setNewParty] = useState<number | undefined>();

    const load = useCallback(async () => {
      setLoading(true);
      try {
        const d = await importAliasAPI.listParties({ party_type: kind, search: search || undefined, include_inactive: inactive, skip: (page - 1) * 20, limit: 20 });
        setRows(d.items || []); setTotal(d.total || 0);
      } catch (e: any) { message.error(e?.response?.data?.detail || 'Errore nel caricamento'); }
      finally { setLoading(false); }
    }, [kind, search, inactive, page]);
    useEffect(() => { load(); }, [load]);

    const saveEdit = async () => {
      if (!editing || !newParty) return;
      try { await importAliasAPI.updateParty(editing.id, { party_id: newParty }); message.success('Corrispondenza aggiornata'); setEditing(null); load(); }
      catch (e: any) { message.error(e?.response?.data?.detail || 'Errore'); }
    };
    const options = (t: string) => (t === 'customer' ? customers.map((c: any) => ({ value: c.id, label: c.company })) : suppliers.map((s: any) => ({ value: s.id, label: s.name })));

    return (
      <div>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select allowClear placeholder="Tipo" style={{ width: 160 }} value={kind} onChange={(v) => { setKind(v); setPage(1); }}
            options={[{ value: 'customer', label: 'Clienti' }, { value: 'supplier', label: 'Fornitori' }]} />
          <Input.Search placeholder="Nome nel documento" allowClear style={{ width: 280 }} onSearch={(v) => { setSearch(v); setPage(1); }} />
          <span><Switch checked={inactive} onChange={setInactive} /> <Text type="secondary">mostra disattivate</Text></span>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Aggiorna</Button>
          <Text type="secondary">{total} corrispondenze</Text>
        </Space>
        <Table size="small" rowKey="id" loading={loading} dataSource={rows}
          pagination={{ current: page, pageSize: 20, total, onChange: setPage }}
          columns={[
            { title: 'Tipo', dataIndex: 'party_type', width: 100, render: (v: string) => <Tag color={v === 'customer' ? 'blue' : 'purple'}>{v === 'customer' ? 'Cliente' : 'Fornitore'}</Tag> },
            { title: 'Nome nel documento', dataIndex: 'original_name', ellipsis: true, render: (v: string, r: any) => v || r.normalized_name },
            { title: 'Anagrafica NAVI', width: 300, render: (r: any) => r.party ? <b>{r.party.name}</b> : '-' },
            { title: 'Usi', dataIndex: 'use_count', width: 60, align: 'center' as const },
            { title: 'Ultimo uso', dataIndex: 'last_used_at', width: 140, render: fmtDate },
            { title: 'Confermata da', dataIndex: 'confirmed_by_username', width: 110, render: (v: string) => v || '-' },
            { title: 'Attiva', width: 70, render: (r: any) => <Switch size="small" checked={r.is_active} onChange={async (v) => { await importAliasAPI.updateParty(r.id, { is_active: v }); load(); }} /> },
            { title: '', width: 90, render: (r: any) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(r); setNewParty(r.party_id); }} />
                <Popconfirm title="Eliminare la corrispondenza?" onConfirm={async () => { await importAliasAPI.deleteParty(r.id); load(); }}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
              </Space>) },
          ]} />
        <Modal open={!!editing} title="Cambia anagrafica della corrispondenza" onOk={saveEdit} onCancel={() => setEditing(null)} okText="Salva" cancelText="Annulla">
          {editing && (
            <div>
              <p><Text type="secondary">Nome nel documento:</Text> {editing.original_name || editing.normalized_name}</p>
              <Select style={{ width: '100%' }} showSearch optionFilterProp="label" value={newParty} onChange={setNewParty} options={options(editing.party_type)} />
            </div>
          )}
        </Modal>
      </div>
    );
  };

  // ------------------------------------------------------------ sigle
  const MarksTab: React.FC = () => {
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [supplierId, setSupplierId] = useState<number | undefined>();
    const [search, setSearch] = useState('');
    const [inactive, setInactive] = useState(false);
    const [page, setPage] = useState(1);
    const [editing, setEditing] = useState<any | null>(null);
    const [newOrder, setNewOrder] = useState<number | undefined>();
    const [newCustomer, setNewCustomer] = useState<number | undefined>();

    const load = useCallback(async () => {
      setLoading(true);
      try {
        const d = await importAliasAPI.listMarks({ supplier_id: supplierId, search: search || undefined, include_inactive: inactive, skip: (page - 1) * 20, limit: 20 });
        setRows(d.items || []); setTotal(d.total || 0);
      } catch (e: any) { message.error(e?.response?.data?.detail || 'Errore nel caricamento'); }
      finally { setLoading(false); }
    }, [supplierId, search, inactive, page]);
    useEffect(() => { load(); }, [load]);

    const saveEdit = async () => {
      if (!editing) return;
      try {
        const payload: any = newOrder ? { customer_order_id: newOrder } : { clear_order: true, customer_id: newCustomer };
        if (!newOrder && !newCustomer) { message.warning('Indica un ordine cliente o un cliente'); return; }
        await importAliasAPI.updateMark(editing.id, payload); message.success('Sigla aggiornata'); setEditing(null); load();
      } catch (e: any) { message.error(e?.response?.data?.detail || 'Errore'); }
    };

    return (
      <div>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select allowClear placeholder="Fornitore" style={{ width: 280 }} showSearch optionFilterProp="label"
            value={supplierId} onChange={(v) => { setSupplierId(v); setPage(1); }}
            options={suppliers.map((s: any) => ({ value: s.id, label: s.name }))} />
          <Input.Search placeholder="Sigla" allowClear style={{ width: 200 }} onSearch={(v) => { setSearch(v); setPage(1); }} />
          <span><Switch checked={inactive} onChange={setInactive} /> <Text type="secondary">mostra disattivate</Text></span>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Aggiorna</Button>
          <Text type="secondary">{total} sigle</Text>
        </Space>
        <Table size="small" rowKey="id" loading={loading} dataSource={rows}
          pagination={{ current: page, pageSize: 20, total, onChange: setPage }}
          columns={[
            { title: 'Fornitore', dataIndex: 'supplier_name', width: 220, ellipsis: true },
            { title: 'Sigla', dataIndex: 'mark', width: 120, render: (v: string) => <Tag>{v}</Tag> },
            { title: 'Ordine cliente / stock', width: 260, render: (r: any) => r.customer_order ? (
              <span><b>{r.customer_order.order_number}</b> {r.customer_order.reorder && <Tag color="gold">stock</Tag>}
                {![1, 2, 3, 4, 5].includes(r.customer_order.status_id) && <Tag color="red">non aperto</Tag>}</span>) : <Text type="secondary">solo cliente</Text> },
            { title: 'Cliente', render: (r: any) => r.customer?.company || '-' },
            { title: 'Usi', dataIndex: 'use_count', width: 60, align: 'center' as const },
            { title: 'Ultimo uso', dataIndex: 'last_used_at', width: 140, render: fmtDate },
            { title: 'Attiva', width: 70, render: (r: any) => <Switch size="small" checked={r.is_active} onChange={async (v) => { await importAliasAPI.updateMark(r.id, { is_active: v }); load(); }} /> },
            { title: '', width: 90, render: (r: any) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(r); setNewOrder(r.customer_order_id || undefined); setNewCustomer(r.customer_id || undefined); }} />
                <Popconfirm title="Eliminare la sigla?" onConfirm={async () => { await importAliasAPI.deleteMark(r.id); load(); }}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
              </Space>) },
          ]} />
        <Modal open={!!editing} title="Destinazione della sigla" onOk={saveEdit} onCancel={() => setEditing(null)} okText="Salva" cancelText="Annulla">
          {editing && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <div><Text type="secondary">Sigla:</Text> <Tag>{editing.mark}</Tag> <Text type="secondary">di {editing.supplier_name}</Text></div>
              <div>
                <Text strong>Ordine cliente / stock aperto</Text>
                <Select allowClear style={{ width: '100%' }} showSearch optionFilterProp="label" value={newOrder} onChange={(v) => setNewOrder(v)}
                  options={openOrders.map((o: any) => ({ value: o.id, label: `${o.order_number} - ${o.customer?.company || ''}` }))} />
              </div>
              <div>
                <Text strong>Oppure solo il cliente</Text>
                <Select allowClear style={{ width: '100%' }} showSearch optionFilterProp="label" value={newCustomer} onChange={(v) => setNewCustomer(v)} disabled={!!newOrder}
                  options={customers.map((c: any) => ({ value: c.id, label: c.company }))} />
              </div>
            </Space>
          )}
        </Modal>
      </div>
    );
  };

  // ------------------------------------------------------------ destinazioni
  const DestinationsTab: React.FC = () => {
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [customerId, setCustomerId] = useState<number | undefined>();
    const [search, setSearch] = useState('');
    const [inactive, setInactive] = useState(false);
    const [page, setPage] = useState(1);
    const [editing, setEditing] = useState<any | null>(null);
    const [newDest, setNewDest] = useState<number | undefined>();
    const [destOptions, setDestOptions] = useState<any[]>([]);

    const load = useCallback(async () => {
      setLoading(true);
      try {
        const d = await importAliasAPI.listDestinations({ customer_id: customerId, search: search || undefined, include_inactive: inactive, skip: (page - 1) * 20, limit: 20 });
        setRows(d.items || []); setTotal(d.total || 0);
      } catch (e: any) { message.error(e?.response?.data?.detail || 'Errore nel caricamento'); }
      finally { setLoading(false); }
    }, [customerId, search, inactive, page]);
    useEffect(() => { load(); }, [load]);

    const openEdit = async (r: any) => {
      setEditing(r); setNewDest(r.shipping_destination_id);
      try { const d = await shippingDestinationAPI.getShippingDestinations(undefined, r.customer_id); setDestOptions(Array.isArray(d) ? d : d?.items || []); }
      catch { setDestOptions([]); }
    };
    const saveEdit = async () => {
      if (!editing || !newDest) return;
      try { await importAliasAPI.updateDestination(editing.id, { shipping_destination_id: newDest }); message.success('Corrispondenza aggiornata'); setEditing(null); load(); }
      catch (e: any) { message.error(e?.response?.data?.detail || 'Errore'); }
    };

    return (
      <div>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select allowClear placeholder="Cliente" style={{ width: 280 }} showSearch optionFilterProp="label"
            value={customerId} onChange={(v) => { setCustomerId(v); setPage(1); }}
            options={customers.map((c: any) => ({ value: c.id, label: c.company }))} />
          <Input.Search placeholder="Nave / destinazione nel documento" allowClear style={{ width: 280 }} onSearch={(v) => { setSearch(v); setPage(1); }} />
          <span><Switch checked={inactive} onChange={setInactive} /> <Text type="secondary">mostra disattivate</Text></span>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Aggiorna</Button>
          <Text type="secondary">{total} corrispondenze</Text>
        </Space>
        <Table size="small" rowKey="id" loading={loading} dataSource={rows}
          pagination={{ current: page, pageSize: 20, total, onChange: setPage }}
          columns={[
            { title: 'Cliente', dataIndex: 'customer_name', width: 220, ellipsis: true },
            { title: 'Nel documento', dataIndex: 'original_text', ellipsis: true, render: (v: string, r: any) => v || r.normalized_text },
            { title: 'Destinazione NAVI', width: 320, render: (r: any) => r.destination ? <b>{r.destination.name}</b> : '-' },
            { title: 'Usi', dataIndex: 'use_count', width: 60, align: 'center' as const },
            { title: 'Ultimo uso', dataIndex: 'last_used_at', width: 140, render: fmtDate },
            { title: 'Attiva', width: 70, render: (r: any) => <Switch size="small" checked={r.is_active} onChange={async (v) => { await importAliasAPI.updateDestination(r.id, { is_active: v }); load(); }} /> },
            { title: '', width: 90, render: (r: any) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                <Popconfirm title="Eliminare la corrispondenza?" onConfirm={async () => { await importAliasAPI.deleteDestination(r.id); load(); }}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
              </Space>) },
          ]} />
        <Modal open={!!editing} title="Cambia destinazione della corrispondenza" onOk={saveEdit} onCancel={() => setEditing(null)} okText="Salva" cancelText="Annulla">
          {editing && (
            <div>
              <p><Text type="secondary">Nel documento:</Text> {editing.original_text || editing.normalized_text}</p>
              <Select style={{ width: '100%' }} showSearch optionFilterProp="label" value={newDest} onChange={setNewDest}
                options={destOptions.map((d: any) => ({ value: d.id, label: d.name }))} />
            </div>
          )}
        </Modal>
      </div>
    );
  };

  return (
    <div className="no-uppercase-modal">
      <Title level={2}><LinkOutlined style={{ marginRight: 8 }} />Corrispondenze delle importazioni</Title>
      <Text type="secondary">
        Le corrispondenze nascono dalle conferme dell'utente nei flussi di importazione da documento: la prima volta che
        una riga, un nome o una sigla viene abbinata a mano, NAVI la ricorda e dalla volta dopo l'abbinamento è automatico.
        Qui si controllano, correggono, disattivano o eliminano.
      </Text>
      <Tabs style={{ marginTop: 16 }} items={[
        { key: 'products', label: 'Prodotti del fornitore', children: <ProductsTab /> },
        { key: 'parties', label: 'Clienti / Fornitori', children: <PartiesTab /> },
        { key: 'marks', label: 'Sigle → destinazione', children: <MarksTab /> },
        { key: 'destinations', label: 'Destinazioni (navi)', children: <DestinationsTab /> },
      ]} />
    </div>
  );
};

export default ImportAliases;
